import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Download } from 'lucide-react';
import { Logo } from '../components/LogoOptions';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [isVerifying, setIsVerifying] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    // Poll payment status
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000; // 2 seconds

      if (attempts >= maxAttempts) {
        setIsVerifying(false);
        setPaymentStatus({ error: 'Verification timeout. Please check your email for confirmation.' });
        return;
      }

      try {
        const response = await axios.get(`${API}/payment/status/${sessionId}`);
        
        if (response.data.success) {
          if (response.data.payment_status === 'paid') {
            setPaymentStatus(response.data);
            setIsVerifying(false);
            return;
          } else if (response.data.status === 'expired') {
            setPaymentStatus({ error: 'Payment session expired. Please try again.' });
            setIsVerifying(false);
            return;
          }
        }

        // Continue polling
        setPollAttempts(attempts + 1);
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setPaymentStatus({ error: 'Error verifying payment. Please contact us.' });
        setIsVerifying(false);
      }
    };

    pollPaymentStatus();
  }, [sessionId, navigate]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Card className="p-12 text-center max-w-md">
          <Loader2 className="w-16 h-16 animate-spin text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your payment</p>
          <p className="text-sm text-gray-500 mt-4">Attempt {pollAttempts + 1} of 10</p>
        </Card>
      </div>
    );
  }

  if (paymentStatus?.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="p-12 text-center max-w-2xl">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Verification Issue</h1>
          <p className="text-lg text-gray-600 mb-8">{paymentStatus.error}</p>
          <div className="space-y-4">
            <Button 
              onClick={() => navigate('/')}
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg w-full"
            >
              Return to Home
            </Button>
            <p className="text-sm text-gray-500">
              Questions? Call us at 206-627-0768 or email info@ezflowtc.com
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Logo />
        </div>
      </header>

      {/* Success Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <Card className="p-12 text-center">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-16 h-16 text-green-600" />
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Payment Successful! 🎉
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              Thank you for your payment. Your transaction has been completed successfully.
            </p>

            <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-8 mb-8">
              <div className="grid md:grid-cols-2 gap-6 text-left">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Amount Paid</p>
                  <p className="text-2xl font-bold text-amber-600">
                    ${(paymentStatus?.amount_total / 100).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Payment Status</p>
                  <p className="text-2xl font-bold text-green-600">PAID</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 text-left">
              <h3 className="font-bold text-lg mb-3 text-blue-900">📧 What Happens Next?</h3>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>A paid invoice has been sent to your email</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Our team will review your transaction details within 24 hours</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>We'll contact you to confirm and begin coordination</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Keep an eye on your inbox for updates</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/')}
                className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg"
              >
                Return to Home
              </Button>
            </div>

            <p className="text-sm text-gray-500 mt-8">
              Transaction ID: {sessionId?.substring(0, 20)}...
            </p>
          </Card>

          <div className="text-center mt-8 text-gray-600">
            <p className="mb-2">Need assistance?</p>
            <p>📞 206-627-0768 | ✉️ info@ezflowtc.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
